// Peças comuns da biblioteca de animações — o vocabulário do CustomGraphics do edvid:
// cartão escuro arredondado com sombra limpa, entrada com blur + subida + back easing,
// traço "a lápis" que se desenha (pathLength/dashoffset), whoosh na entrada, pop nos
// elementos. Tudo em Poppins (já carregada por fonts.ts) e na cor de destaque do vídeo.
import React, { createContext, useContext } from 'react';
import { Easing, Sequence, interpolate, useCurrentFrame } from 'remotion';
import { measureText } from '@remotion/layout-utils';
import { POPPINS } from '../fonts';
import { Sfx } from '../context';

export const FONT = POPPINS;

// ─── Geometria do quadro 1080×1920 ───
/** topo do conteúdo na zona "top" (≈11% — abaixo do trilho de status, cabeça livre do meio pra baixo) */
export const ZONE_TOP_Y = 210;
/**
 * centro vertical da zona "middle": altura do PEITO (~58%), entre o rosto e a
 * legenda (karaoke a 420px da base). Em 864 (45%) caía em cima dos olhos no
 * enquadramento de busto — medido no vídeo de teste.
 */
export const ZONE_MIDDLE_Y = 1110;
/** largura útil: livra o trilho de ações do Reels/TikTok à direita (~130px por lado) */
export const SAFE_W = 820;

// ─── Estilo ───
export const CARD: React.CSSProperties = {
  background: 'linear-gradient(180deg,#1c1f27 0%,#14161b 100%)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: 30,
  boxShadow: '0 18px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
  boxSizing: 'border-box',
  fontFamily: FONT,
  color: '#fff',
};
export const MUTED = 'rgba(255,255,255,0.62)';
export const TEXT_SHADOW = '0 4px 18px rgba(0,0,0,0.55)';
export const DROP = 'drop-shadow(0 6px 14px rgba(0,0,0,0.45))';

export const EASE_OUT = Easing.out(Easing.cubic);
export const EASE_BACK = Easing.out(Easing.back(1.6));
export const EASE_SOFT_BACK = Easing.out(Easing.back(1.25));
export const EASE_IN_OUT = Easing.bezier(0.4, 0, 0.2, 1);

/** 0→1 entre os quadros a e b (clamp), com easing opcional */
export function prog(f: number, a: number, b: number, easing?: (t: number) => number): number {
  if (b <= a) return f >= b ? 1 : 0;
  return interpolate(f, [a, b], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing });
}

// ─── Relógio do gráfico ───
// Regra da capa: nada ANIMA entrando no quadro 0. Um gráfico que começa no 0 roda com o
// relógio adiantado (já assentado) e sem SFX de entrada.
interface Clock { offset: number; silent: boolean }
const ClockCtx = createContext<Clock>({ offset: 0, silent: false });
export const GraphicClock: React.FC<{ offset: number; silent: boolean; children: React.ReactNode }> = ({ offset, silent, children }) => (
  <ClockCtx.Provider value={{ offset, silent }}>{children}</ClockCtx.Provider>
);
/** quadro local do gráfico (0 = início da janela) */
export function useGFrame(): number {
  return useCurrentFrame() + useContext(ClockCtx).offset;
}

/** SFX num quadro local (pop ~0.12 nos elementos, como o edvid). Mudo quando o relógio foi adiantado. */
export const Pop: React.FC<{ at: number; src?: string; volume?: number }> = ({ at, src = 'pop.mp3', volume = 0.12 }) => {
  const { offset, silent } = useContext(ClockCtx);
  const from = Math.round(at - offset);
  if (silent || from < 0) return null;
  return (
    <Sequence from={from} durationInFrames={24} layout="none">
      <Sfx src={src} volume={volume} />
    </Sequence>
  );
};

// ─── Texto ───
/**
 * Maior tamanho (≤ max) em que `text` cabe em `maxWidth` numa linha; nunca abaixo de `min`.
 * measureText só roda depois das fontes carregarem (CustomGraphics espera useFontsReady).
 */
export function fitFont(text: string, opts: { weight: number; max: number; min: number; maxWidth: number; letterSpacing?: number }): number {
  const t = text || ' ';
  const w = measureText({ text: t, fontFamily: FONT, fontSize: 100, fontWeight: String(opts.weight) }).width;
  const extra = (opts.letterSpacing || 0) * t.length;
  const size = w > 0 ? Math.floor(((opts.maxWidth - extra) / w) * 100) : opts.max;
  return Math.max(opts.min, Math.min(opts.max, size));
}

export function textWidth(text: string, size: number, weight: number): number {
  return measureText({ text: text || ' ', fontFamily: FONT, fontSize: size, fontWeight: String(weight) }).width;
}

// ─── Cor ───
function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** texto sobre o destaque: branco, ou quase-preto se o destaque for claro (amarelo, verde-limão…) */
export const onAccent = (hex: string) => (luminance(hex) > 0.45 ? '#111217' : '#ffffff');
/** destaque escurecido o bastante para ler sobre papel claro */
export function inkAccent(hex: string): string {
  let [r, g, b] = rgb(hex);
  let k = 0;
  while (luminance(`#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`) > 0.22 && k < 12) {
    r = Math.round(r * 0.85);
    g = Math.round(g * 0.85);
    b = Math.round(b * 0.85);
    k++;
  }
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
export function alpha(hex: string, a: number): string {
  const [r, g, b] = rgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

// ─── Traço a lápis ───
/** Caminho SVG que se desenha (progress 0..1), ponta redonda, sombra suave. */
export const PencilPath: React.FC<{ d: string; progress: number; color: string; width?: number; shadow?: boolean }> = ({
  d,
  progress,
  color,
  width = 9,
  shadow = true,
}) => (
  <path
    d={d}
    fill="none"
    stroke={color}
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
    pathLength={1}
    strokeDasharray={1}
    strokeDashoffset={1 - Math.max(0, Math.min(1, progress))}
    style={shadow ? { filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.45))' } : undefined}
  />
);

/** Props que todo template recebe. */
export interface GraphicComponentProps<P> {
  props: P;
  /** duração da janela em quadros */
  totalFrames: number;
  /** cor de destaque resolvida (props.accent → captions.accent → hook.accent) */
  accent: string;
}
